import {
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { DefaultButton, ErrorLabel, colors, dateFormat } from 'design-system';
import React, { useCallback, useMemo } from 'react';

import { matchCurrentDate } from 'data-layer-cr/dist/functions/helpers';
import { Quotation } from 'data-layer-cr/dist/models';

// Hooks
import useDownloadPolicy from 'src/hooks/useDownloadPolicy';

// Constants
import {
  EMITTED,
  ERROR_EMISSION,
  PENDING_EMISSION,
  PENDING_INSPECTION,
  PROCESSING_EMISSION,
} from 'src/constants/quotationStatus';

// Models
import QuotationChange from 'src/models/Quotation/QuotationChange';

// Methods
import { statusChangeDateForId } from 'src/functions/quotationStatusMethods';

// Components
import { CopyInspectionLinkButton } from '../../../components';
import BaseHistoryStepCard, { StepStatus } from '../BaseHistoryStepCard';

const { prettyDateFromServerString } = dateFormat;

type Props = {
  quotation: Quotation;
  changes: QuotationChange[];
};

const useStyles = makeStyles({
  smallText: {
    color: colors.abbey,
  },
  disabledText: {
    color: colors.abbey,
  },
});

function isDisabled(status: string): boolean {
  return ['pending', 'expired'].includes(status);
}

/**
 * This card is considered to be active when the quotation is in any state greater
 * than PENDING_INSPECTION, and will configure itself accordingly to said state.
 */
const EmissionStepCard = ({ quotation, changes }: Props): JSX.Element => {
  /**
   * General properties
   */
  const classes = useStyles();
  const emissionUrl = useMemo(
    () => `${process.env.REACT_APP_INSPECTION_URL}${quotation.urlSalt}`,
    [quotation],
  );

  const { onRequestDownloadPdf, requestStatus } = useDownloadPolicy(quotation);

  /**
   * Callback that redirects the user to the emission application
   */
  const goToEmission = useCallback(() => {
    window.open(emissionUrl, '_blank');
  }, [emissionUrl]);

  /**
   * The status that will be used for the card
   */
  const usedStatus: StepStatus = useMemo(() => {
    if (matchCurrentDate(quotation.expireAt)) {
      return 'expired';
    }
    switch (quotation.status.id) {
      case PENDING_INSPECTION:
        return 'pending';
      case ERROR_EMISSION:
        return 'active';
      case PENDING_EMISSION:
        return 'active';
      default:
        return 'completed';
    }
  }, [quotation]);

  /**
   * The title that will be rendered in the card
   */
  const usedTitle: React.ReactNode = useMemo(() => {
    switch (quotation.status.id) {
      case PROCESSING_EMISSION:
        return (
          <>
            Emisión en <b>proceso</b>
          </>
        );
      case EMITTED:
        return (
          <>
            Póliza <b>lista!</b>
          </>
        );
      default:
        return <b>Por emisión</b>;
    }
  }, [quotation]);

  /**
   * The used text for the subtitle
   */
  const usedSubtitle: string = useMemo(() => {
    if (
      [PENDING_INSPECTION, PENDING_EMISSION, ERROR_EMISSION].includes(
        quotation.status.id,
      )
    ) {
      return 'Puedes copiar el link y enviárselo a tu cliente por WhatsApp o por email, para que realice el pago de la póliza.';
    }
    const foundDate = statusChangeDateForId(quotation.status.id, changes);
    if (foundDate) {
      return prettyDateFromServerString(foundDate, false);
    }
    return '';
  }, [quotation, changes]);

  /**
   * This is the content that includes all possible buttons
   */
  const withAllButtonsContent = useMemo(
    () => (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <CopyInspectionLinkButton
            id="sales-web-button-inspectionlink"
            data-testid="copy-payment-link-button"
            quotation={quotation}
            disabled={isDisabled(usedStatus)}
            emissionLink
          />
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          <Typography variant="caption" className={classes.smallText}>
            o si prefieres puedes
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <DefaultButton
            id="sales-web-button-gotoemission"
            data-testid="go-to-emission-button"
            fullWidth
            disabled={isDisabled(usedStatus)}
            onClick={goToEmission}
          >
            Emitir póliza
          </DefaultButton>
        </Grid>
      </Grid>
    ),
    [quotation, usedStatus, classes, goToEmission],
  );

  /**
   * This is the content that will only render the download policy button
   */
  const withDownloadPolicyButton = useMemo(
    () => (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DefaultButton
            id="sales-web-button-downloadpolicy"
            data-testid="download-policy-button"
            fullWidth
            variant="outlined"
            onClick={onRequestDownloadPdf}
          >
            {requestStatus === 'requesting' ? (
              <CircularProgress data-testid="network-ind-pdf" size={20} />
            ) : (
              <>Descargar póliza</>
            )}
          </DefaultButton>
        </Grid>
        {requestStatus === 'failure' && (
          <Grid item xs={12} container justifyContent="center">
            <ErrorLabel data-testid="download-policy-error">
              No se pudo obtener la póliza, por favor intente de nuevo.
            </ErrorLabel>
          </Grid>
        )}
      </Grid>
    ),
    [requestStatus, onRequestDownloadPdf],
  );

  /**
   * The content that will actually be rendered inside the card
   */
  const usedContent: React.ReactNode = useMemo(() => {
    if (
      [PENDING_INSPECTION, PENDING_EMISSION, ERROR_EMISSION].includes(
        quotation.status.id,
      )
    ) {
      return withAllButtonsContent;
    }
    if (quotation.status.id === EMITTED) {
      return withDownloadPolicyButton;
    }
    return <div />;
  }, [quotation, withAllButtonsContent, withDownloadPolicyButton]);

  return (
    <BaseHistoryStepCard
      title={usedTitle}
      subTitle={usedSubtitle}
      status={usedStatus}
    >
      {usedContent}
    </BaseHistoryStepCard>
  );
};

export default EmissionStepCard;
